/**
 * f13.2: the cycle submission's read, its one write and the file download.
 *
 * The submit POSTs the version the caller last read. On `201` the cycle, the
 * cycle list, the review and the submission record are invalidated and the
 * submitted file downloads at once (blob through the typed client, the bearer
 * token attached there). A `409 CYCLE_VERSION_CONFLICT` refetches the cycle.
 * Nothing here decides who may submit: the server does.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, type Cycle } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { saveBlob } from '@/features/cycle-export/useCycleExport';

export type SubmissionOutcome = { tone: 'success' | 'error'; message: string };

const VERSION_CONFLICT = 'CYCLE_VERSION_CONFLICT';

/** True when the server offers this caller the submission (Finance on a Frozen cycle). */
export function canSubmit(cycle: Cycle): boolean {
  return cycle.available_transitions.some((edge) => edge.action === 'SUBMIT');
}

export function submittedMessage(code: string, rowCount: number): string {
  return `Cycle ${code} submitted — ${rowCount} engagement${rowCount === 1 ? '' : 's'}`;
}

export function submitFailureMessage(error: unknown): string {
  if (error instanceof ApiError && error.reason === VERSION_CONFLICT) return 'The cycle changed — refreshed';
  return error instanceof Error ? error.message : 'The cycle could not be submitted.';
}

/** The submission record. Finance and Admin; others are refused `403` and see nothing. */
export function useCycleSubmission(cycleId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.cycle.submission(cycleId),
    queryFn: () => api.getCycleSubmission(cycleId),
    enabled: enabled && Boolean(cycleId),
    retry: false,
  });
}

/** Fetch the exact submitted bytes and hand them to the browser. */
export async function downloadSubmission(cycleId: string, fileName: string): Promise<void> {
  saveBlob(await api.downloadCycleSubmission(cycleId), fileName);
}

export function useSubmitCycle(cycleId: string, cycleCode: string) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<SubmissionOutcome | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.cycles.all() });
    void queryClient.invalidateQueries({ queryKey: ['review', cycleId] });
  };

  const submit = useMutation({
    mutationFn: (expectedVersion: number) => api.submitCycle(cycleId, { expected_version: expectedVersion }),
    onMutate: () => setOutcome(null),
    onSuccess: async (result) => {
      invalidate();
      setOutcome({ tone: 'success', message: submittedMessage(cycleCode, result.row_count) });
      try {
        await downloadSubmission(cycleId, result.file_name);
      } catch (error) {
        setOutcome({
          tone: 'error',
          message: `Cycle ${cycleCode} submitted, but the file could not be downloaded: ${
            error instanceof Error ? error.message : 'try the Submission card'
          }`,
        });
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) invalidate();
      setOutcome({ tone: 'error', message: submitFailureMessage(error) });
    },
  });

  return { submit, outcome, dismiss: () => setOutcome(null) };
}
